import React, { useEffect, useState, useMemo } from "react";
import api from "../../lib/api";
import { LoadErrorBox } from "../../components/common/LoadErrorBox";
import {
  StatsRow, StatCard, Card, CardTitle, Alert, Section,
} from "../../components/result/Primitives";
import { Button } from "../../components/ui/button";
import { RefreshCw, Download, AlertCircle } from "lucide-react";
import { toast } from "sonner";
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid,
} from "recharts";

const fmtEur = (cents) =>
  `€ ${(Number(cents || 0) / 100).toLocaleString("it-IT", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const fmtEurCompact = (cents) => {
  const eur = Number(cents || 0) / 100;
  if (Math.abs(eur) >= 1000000) return `€ ${(eur / 1000000).toLocaleString("it-IT", { maximumFractionDigits: 1 })}M`;
  if (Math.abs(eur) >= 1000) return `€ ${(eur / 1000).toLocaleString("it-IT", { maximumFractionDigits: 1 })}k`;
  return `€ ${eur.toLocaleString("it-IT", { maximumFractionDigits: 0 })}`;
};
const fmtInt = (n) => Number(n || 0).toLocaleString("it-IT");

export default function SuperAdminCosts() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/superadmin/costs/summary");
      setData(data);
    } catch { toast.error("Errore nel caricamento"); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const chartData = useMemo(() => {
    if (!data?.by_service) return [];
    return data.by_service.slice(0, 8).map((s) => ({
      name: s.name.length > 22 ? s.name.slice(0, 22) + "…" : s.name,
      fullName: s.name,
      cost: s.total_cost_cents / 100,
      calls: s.calls_month,
    }));
  }, [data]);

  const exportCsv = () => {
    if (!data?.by_service?.length) return;
    const rows = [
      ["Servizio", "Categoria", "Costo unitario (cent)", "Chiamate mese", "Cache hits", "Costo totale (€)"],
      ...data.by_service.map((s) => [
        s.name, s.category, s.unit_cost_cents, s.calls_month, s.cache_hits,
        (s.total_cost_cents / 100).toFixed(2),
      ]),
    ];
    const csv = rows.map((r) => r.join(";")).join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = "costi-openapi.csv"; a.click();
    URL.revokeObjectURL(url);
  };

  if (loading) return <div className="text-sm text-text-muted">Caricamento…</div>;
  if (!data) return <LoadErrorBox what="i costi" onRetry={load} testId="sa-costs-load-error" />;

  const cacheRatio = data.month_calls > 0 ? Math.round((data.cache_hits / data.month_calls) * 100) : 0;

  return (
    <div className="space-y-8">
      <div className="flex items-start justify-between flex-wrap gap-4">
        <div>
          <div className="text-[11px] font-medium tracking-widest uppercase text-text-muted mb-2">Wattline · Super admin</div>
          <h1 className="text-3xl font-semibold text-text-primary tracking-tight">Costi OpenAPI</h1>
          <p className="text-base text-text-secondary mt-2">Monitoraggio costi reali della piattaforma</p>
        </div>
        <Button variant="outline" onClick={load} className="h-10 rounded-md border-border" data-testid="sa-costs-refresh">
          <RefreshCw size={14} className="mr-1.5" /> Aggiorna
        </Button>
      </div>

      <StatsRow columns={4}>
        <StatCard label="Costo mese" value={fmtEurCompact(data.month_cost_cents)}
                  detail={`${fmtInt(data.month_calls)} chiamate`} testId="sa-costs-month" />
        <StatCard label="Costo medio" value={fmtEur(data.avg_cost_cents)}
                  detail="Per chiamata" testId="sa-costs-avg" />
        <StatCard label="Cache hits" value={fmtInt(data.cache_hits)}
                  detail={`${cacheRatio}% del totale`} detailColor={cacheRatio > 30 ? "success" : "default"}
                  testId="sa-costs-cache" />
        <StatCard label="Risparmio cache" value={fmtEurCompact(data.cache_savings_cents)}
                  detail="Costi evitati" detailColor="success" testId="sa-costs-savings" />
      </StatsRow>

      {data.most_expensive && (
        <Alert variant="warning" testId="sa-costs-most-expensive">
          <strong>Servizio più costoso del mese:</strong> {data.most_expensive.name} —
          {" "}{fmtEur(data.most_expensive.total_cost_cents)} su {fmtInt(data.most_expensive.calls_month)} chiamate
          ({fmtEur(data.most_expensive.unit_cost_cents)} cad.)
        </Alert>
      )}

      <Card testId="sa-costs-chart-card">
        <CardTitle>Costi per servizio (top 8 del mese)</CardTitle>
        {chartData.length === 0 ? (
          <div className="text-sm text-text-muted py-12 text-center">Nessuna chiamata registrata questo mese</div>
        ) : (
          <div style={{ width: "100%", height: 320 }}>
            <ResponsiveContainer>
              <BarChart data={chartData} margin={{ top: 8, right: 16, left: 0, bottom: 50 }}>
                <CartesianGrid stroke="var(--border)" strokeDasharray="3 3" vertical={false} />
                <XAxis dataKey="name" tick={{ fontSize: 11, fill: "var(--text-muted)" }} angle={-25} textAnchor="end" interval={0} height={60} axisLine={false} tickLine={false} />
                <YAxis tickFormatter={(v) => `€${Math.round(v).toLocaleString("it-IT")}`}
                       tick={{ fontSize: 11, fill: "var(--text-muted)" }} axisLine={false} tickLine={false} width={70} />
                <Tooltip formatter={(v, n) => n === "cost" ? `€ ${Number(v).toLocaleString("it-IT", { minimumFractionDigits: 2 })}` : v}
                         labelFormatter={(l, p) => p?.[0]?.payload?.fullName || l} />
                <Bar dataKey="cost" fill="#3B82F6" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        )}
      </Card>

      <Card testId="sa-costs-table-card">
        <div className="flex items-center justify-between mb-4">
          <CardTitle>Dettaglio per servizio</CardTitle>
          {data.by_service.length > 0 && (
            <Button variant="outline" size="sm" onClick={exportCsv} className="rounded-md border-border h-8" data-testid="sa-costs-export">
              <Download size={13} className="mr-1.5" /> Esporta CSV
            </Button>
          )}
        </div>
        {data.by_service.length === 0 ? (
          <div className="text-sm text-text-muted py-12 text-center">Nessun dato disponibile</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="sa-costs-table">
              <thead className="bg-subtle text-text-secondary text-xs uppercase tracking-wider">
                <tr>
                  <th className="text-left px-4 py-2.5 font-medium">Servizio</th>
                  <th className="text-left px-4 py-2.5 font-medium">Categoria</th>
                  <th className="text-right px-4 py-2.5 font-medium">Costo unit.</th>
                  <th className="text-right px-4 py-2.5 font-medium">Chiamate</th>
                  <th className="text-right px-4 py-2.5 font-medium">Cache</th>
                  <th className="text-right px-4 py-2.5 font-medium">Costo totale</th>
                </tr>
              </thead>
              <tbody>
                {data.by_service.map((s) => (
                  <tr key={s.service_id} className="border-t border-border hover:bg-subtle" data-testid={`sa-costs-row-${s.service_id}`}>
                    <td className="px-4 py-2.5 text-text-primary font-medium">{s.name}</td>
                    <td className="px-4 py-2.5 text-text-secondary text-xs">{s.category}</td>
                    <td className="px-4 py-2.5 text-right tabular-nums text-text-secondary">{fmtEur(s.unit_cost_cents)}</td>
                    <td className="px-4 py-2.5 text-right tabular-nums">{fmtInt(s.calls_month)}</td>
                    <td className="px-4 py-2.5 text-right tabular-nums text-success-strong">{fmtInt(s.cache_hits)}</td>
                    <td className="px-4 py-2.5 text-right tabular-nums font-medium">{fmtEur(s.total_cost_cents)}</td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr className="border-t-2 border-border bg-subtle/50 font-semibold">
                  <td className="px-4 py-2.5" colSpan={3}>Totale</td>
                  <td className="px-4 py-2.5 text-right tabular-nums">{fmtInt(data.month_calls)}</td>
                  <td className="px-4 py-2.5 text-right tabular-nums text-success-strong">{fmtInt(data.cache_hits)}</td>
                  <td className="px-4 py-2.5 text-right tabular-nums">{fmtEur(data.month_cost_cents)}</td>
                </tr>
              </tfoot>
            </table>
          </div>
        )}
      </Card>

      <Section>
        <Alert variant="info" icon={AlertCircle}>
          Per modificare i <strong>costi unitari</strong> dei servizi vai a <a href="/superadmin/pricing" className="text-accent hover:underline font-medium">Piani e prezzi → Costi OpenAPI</a>.
          Aggiornali quando OpenAPI cambia i listini.
        </Alert>
      </Section>
    </div>
  );
}
